/**
 * 應用程式鍵值儲存（IndexedDB），取代 localStorage。
 * 啟動時會將舊版 localStorage 中已知鍵遷移進來並清除對應 localStorage。
 */

const DB_NAME = 'mygenie_app';
const DB_VERSION = 1;
const STORE_NAME = 'kv';

/** 已知需從 localStorage 遷移的鍵（含 pendingToolList_* 前綴另行掃描） */
const MIGRATION_KEYS = new Set([
  'nowIsWebSocket',
  'chatHistory',
  'mygenie_token',
  'authToken',
  'lastBackendSync',
  'mygenie_auth',
  'mygenie_refresh_token',
  'mygenie_last_domain',
]);

let dbPromise = null;
let readyPromise = null;

/** 供需在 render 路徑同步讀取的鍵（於 ready() 後與每次 get/set 更新） */
const syncMirror = Object.create(null);

function hasIndexedDB() {
  return typeof indexedDB !== 'undefined';
}

function openDb() {
  if (!hasIndexedDB()) {
    return Promise.reject(new Error('indexedDB unavailable'));
  }
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };
    });
  }
  return dbPromise;
}

async function idbPutRaw(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put({ key, value });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function migrateFromLocalStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const keysToMigrate = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith('pendingToolList_') || MIGRATION_KEYS.has(k)) {
        keysToMigrate.push(k);
      }
    }
    for (const key of keysToMigrate) {
      const val = localStorage.getItem(key);
      if (val !== null) {
        await idbPutRaw(key, val);
        syncMirror[key] = val;
      }
      localStorage.removeItem(key);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('appIndexedDB migrateFromLocalStorage', e);
  }
}

async function hydrateMirrorFromDb() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => {
      const rows = req.result || [];
      rows.forEach((row) => {
        if (row && row.key !== undefined && row.value !== undefined) {
          syncMirror[row.key] = row.value;
        }
      });
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * 開啟資料庫、遷移舊 localStorage、載入 syncMirror。請在 React root.render 之前 await。
 */
export async function ready() {
  if (!hasIndexedDB()) {
    return;
  }
  if (readyPromise) return readyPromise;
  readyPromise = (async () => {
    await openDb();
    await migrateFromLocalStorage();
    await hydrateMirrorFromDb();
  })();
  return readyPromise;
}

/** 與 localStorage 相同：無鍵時回傳 null */
export function getItemSync(key) {
  return Object.prototype.hasOwnProperty.call(syncMirror, key) ? syncMirror[key] : null;
}

export async function getItem(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => {
      const row = req.result;
      const val = row ? row.value : null;
      if (val !== null) syncMirror[key] = val;
      else delete syncMirror[key];
      resolve(val);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function setItem(key, value) {
  syncMirror[key] = value;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put({ key, value });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function removeItem(key) {
  delete syncMirror[key];
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** 刪除所有 key 以指定前綴開頭的項目，回傳刪除數量 */
export async function removeItemsByPrefix(prefix) {
  const db = await openDb();
  const keysToDelete = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      const keys = req.result || [];
      resolve(keys.filter((k) => typeof k === 'string' && k.startsWith(prefix)));
    };
    req.onerror = () => reject(req.error);
  });
  if (keysToDelete.length === 0) return 0;
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    keysToDelete.forEach((key) => {
      delete syncMirror[key];
      store.delete(key);
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  return keysToDelete.length;
}
